import { useState } from "react";
import { useNavigate } from "react-router";
import Spinner from "../Spinner";
import { useApi } from "../../api/ApiProvider";
import Api from "../../axiosApi/api";
import { useToastMessageQueue } from "../../components/ToastMessages/ToastMessageProvider";
import intl from "components/i18n/ReactIntlWrapper";
import {
  Typography,
  Box,
  Button,
  Card
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import { DEFAULT_PAGINATION, useSearch } from 'hooks/useSearch';
import { TimeOffRegimeDTO } from '../../axiosApi/models/time-off-regime-dto';
import SearchInput from "components/SearchInput";
import { handleAPIError } from "common/errorHandler";
import DefaultActions from "components/DataGrid/DefaultActions";
import { getUserEntity } from "common/UserEntityProvider";
import { useDialog } from "common/DialogProvider";

const Regime = () => {
  const [errors, setErrors] = useState({});
  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const navigate = useNavigate();
  const { openDialog, closeDialog } = useDialog();
  const [searchTerm, setSearchTerm] = useState<string>("");

  const handleClearSearch = () => {
    setSearchTerm('');
  };
  const entityType = getUserEntity();

  const INITIAL_STATE: GridInitialState = {
    pagination: {
      paginationModel: {
        page: DEFAULT_PAGINATION.pageNum,
        pageSize: DEFAULT_PAGINATION.pageSize,
      },
    },
    sorting: {
      sortModel: [
        {
          field: 'name',
          sort: 'asc',
        },
      ],
    },
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: intl.get('timeOffRegime.table.thead.name'),
      width: 250,
      flex: 1,
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <DefaultActions
          row={params.row}
          entityType={entityType.entityType}
          onEdit={() => navigate(`/regime/${params.row.id}/types`)}
          onDelete={() => handleDelete(params.row.id)}
          identifier="ui.timeoff.regime.actions"
        />
      ),
    },
  ];

  const handleDelete = (id: number) => {
    const regime = data?.queryResult?.find(r => r.id === id);
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('timeOffRegime.dialog.delete.message', { name: regime?.name || 'this regime' }),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get("delete.modal.delete.button"),
        handleConfirm: async () => {
          try {
            const response = await api.timeoffApi.apiVversionTimeOffRegimeIdDelete(
              id,
              "1"
            );
            if (response.status === 200) {
              toast.success({
                message: intl.get("timeOffRegime.toast.success.delete")
              });
              closeDialog(dialogId);
              reFetchData();
            } else {
              toast.error({
                header: intl.get("timeOffRegime.toast.error.delete")
              });
              closeDialog(dialogId);
            }
          } catch (error) {
            toast.error({
              header: intl.get("timeOffRegime.toast.error.delete"),
              body: error.message
            });
            closeDialog(dialogId);
          }
        }
      },
      cancelButton: {
        cancelLabel: intl.get("delete.modal.cancel.button"),
        handleCancel: () => {
          closeDialog(dialogId);
        },
      },
    });
  };

  const apiPromise = (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string
  ) => {
    return api.timeoffApi.apiVversionTimeOffRegimeSearchGet("1", searchTerm, pageNum, pageSize, sort, order);
  };

  const processError = (error: any) => {
    handleAPIError(error, toast, errors);
    setErrors({ ...errors });
  };

  const { data, loading, onPaginationModelChange, handleSortModelChange, reFetchData, paginationModel } = useSearch<TimeOffRegimeDTO>({
    apiPromise: apiPromise,
    searchTerm: searchTerm,
    defaultSort: { sort: 'Name', order: 'asc' },
    onError: processError,
    pageKey: 'timeOffRegimes',
  });

  return (
    <Card sx={{ m: 2 }}>
    <Box sx={{ p: 3 }}>
      <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
        {intl.get("timeOffRegime.header")}
      </Typography>
      <Box sx={{ p: 3 }}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
          <Box sx={{ minWidth: 300 }}>
            <SearchInput value={searchTerm} onChange={setSearchTerm} onClear={handleClearSearch} />
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/regime/new')}
            sx={{ height: 40 }}
          >
            <AddIcon /> {intl.get('regime.new') ? intl.get('regime.new') : intl.get('button.new')}
          </Button>
        </Box>
        <Box style={{ width: '100%', display: 'flex' }}>
          {loading && <Spinner />}
          {(data?.queryResult || (!loading && data?.queryResult === null)) && (
            <DataGrid
              rows={data?.queryResult || []}
              columns={columns as GridColDef[]}
              rowCount={data?.totalRecords || 0}
              loading={loading}
              paginationModel={paginationModel}
              onPaginationModelChange={onPaginationModelChange}
              onSortModelChange={handleSortModelChange}
            />
          )}
        </Box>
      </Box>
    </Box>
    </Card>
  );
};

export default Regime;
